"use client";

import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Check, Clock, Loader2, Pencil, Plus, Send } from "lucide-react";
import {
  createWebhookEndpoint,
  fetchWebhookEventTypes,
  updateWebhookEndpoint,
  type ApiError,
  type WebhookEndpoint,
  type WebhookEventType,
} from "../../../services/merchantApi";
import { DevModal, FormField, InlineNotice, Switch, focusRing, ghostBtn, inputCls, primaryBtn } from "../ui";
import { formatCountdown } from "./format";
import { notifyWebhooksChanged } from "./useWebhookSummary";

// ────────────────────────────────────────────────────────────────────────────
// EndpointFormModal — create and edit a webhook endpoint.
//
// Never optimistic: the server re-validates the URL (DNS included) on every
// create and URL change, so the submit button reads "Checking URL…" and the
// caller only ever receives the endpoint the server returned.
//
// This module also hosts the few helpers every endpoint modal/card shares
// (error parsing, the 429 countdown, the event-type catalogue). They live in
// this leaf module so the modals have one place to import them from without
// import cycles.
// ────────────────────────────────────────────────────────────────────────────

// ─── Shared helpers ────────────────────────────────────────────────────────

/** Normalises anything a failed request threw into an ApiError. */
export function apiErrorOf(e: unknown): ApiError {
  if (e instanceof Error) return e as ApiError;
  return new Error(typeof e === "string" && e ? e : "Something went wrong.") as ApiError;
}

/** Save-time URL errors render under the URL field (spec §0.2). */
export function isUrlErrorCode(code: string | null | undefined): boolean {
  return !!code && (code.startsWith("webhook_url_") || code.startsWith("dns_"));
}

export const UNAVAILABLE_MESSAGE =
  "Webhooks are temporarily paused on our side. Please try again in a few minutes.";

/**
 * 429 countdown. `start(err.retryAfter)` disables the control and shows
 * "Try again in 0:42"; it re-enables itself at 0:00. Without a Retry-After
 * value it waits 60 s and reads "Try again in a minute."
 */
export function useCountdown(): {
  active: boolean;
  remaining: number;
  text: string;
  start: (seconds?: number | null) => void;
} {
  const [until, setUntil] = useState<number | null>(null);
  const [approx, setApprox] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (until === null) return;
    const id = setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= until) setUntil(null);
    }, 250);
    return () => clearInterval(id);
  }, [until]);

  const start = useCallback((seconds?: number | null) => {
    const exact = typeof seconds === "number" && Number.isFinite(seconds) && seconds > 0;
    const t = Date.now();
    setNow(t);
    setApprox(!exact);
    setUntil(t + (exact ? Math.ceil(seconds as number) : 60) * 1000);
  }, []);

  const remaining = until === null ? 0 : Math.max(0, Math.ceil((until - now) / 1000));
  const active = remaining > 0;
  // No trailing period: the text doubles as a button label.
  const text = !active ? "" : approx ? "Try again in a minute" : `Try again in ${formatCountdown(remaining)}`;
  return { active, remaining, text, start };
}

export interface EventTypeInfo {
  type: WebhookEventType;
  description: string;
  /** Example payload from GET /event-types; null when the catalogue could not be loaded. */
  samplePayload: Record<string, unknown> | null;
}

// Used until (or instead of, if it fails) the live catalogue arrives, so the
// form never blocks on it. Descriptions match the backend catalogue.
export const FALLBACK_EVENT_TYPES: EventTypeInfo[] = [
  {
    type: "checkout.session.completed",
    description: "Payment received and settled to your wallet.",
    samplePayload: null,
  },
  {
    type: "checkout.session.expired",
    description: "The session timed out without a confirmed payment.",
    samplePayload: null,
  },
  {
    type: "checkout.session.failed",
    description: "A payment arrived but settlement failed on our side.",
    samplePayload: null,
  },
];

const EVENT_ORDER: string[] = FALLBACK_EVENT_TYPES.map((t) => t.type);

// Catalogue order for a list of subscribed types (unknown types go last).
function sortEvents(list: string[]): string[] {
  const rank = (t: string) => {
    const i = EVENT_ORDER.indexOf(t);
    return i === -1 ? EVENT_ORDER.length : i;
  };
  return [...list].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}

// The catalogue is identical for every merchant: fetched once per page load.
let eventTypesCache: EventTypeInfo[] | null = null;
let eventTypesRequest: Promise<EventTypeInfo[] | null> | null = null;

/**
 * The event-type catalogue (descriptions + sample payloads). Returns the
 * static fallback immediately and swaps in the live list when it arrives.
 */
export function useEventTypes(
  wallet: string | null,
  email: string | null,
): { types: EventTypeInfo[]; live: boolean; loading: boolean } {
  const [types, setTypes] = useState<EventTypeInfo[] | null>(eventTypesCache);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (eventTypesCache || !wallet) return;
    let alive = true;
    if (!eventTypesRequest) {
      const req: Promise<EventTypeInfo[] | null> = fetchWebhookEventTypes(wallet, email)
        .then((res) => {
          const list = (res?.eventTypes || [])
            .filter((t) => t && typeof t.type === "string")
            .map((t) => ({
              type: t.type,
              description: t.description || "",
              samplePayload:
                t.samplePayload && typeof t.samplePayload === "object" ? t.samplePayload : null,
            }));
          if (list.length === 0) return null;
          eventTypesCache = list;
          return list;
        })
        .catch(() => null);
      eventTypesRequest = req;
      void req.finally(() => {
        if (eventTypesRequest === req) eventTypesRequest = null;
      });
    }
    eventTypesRequest.then((list) => {
      if (!alive) return;
      if (list) setTypes(list);
      else setFailed(true);
    });
    return () => {
      alive = false;
    };
  }, [wallet, email]);

  return { types: types || FALLBACK_EVENT_TYPES, live: !!types, loading: !types && !failed };
}

// ─── Form ──────────────────────────────────────────────────────────────────

const URL_PLACEHOLDER = "https://api.yourstore.com/webhooks/cashtap";
const DESCRIPTION_MAX = 200;
const URL_MAX = 2048;

function validateUrl(raw: string): string | null {
  const v = raw.trim();
  if (!v) return "Enter the HTTPS URL of your endpoint.";
  if (v.length > URL_MAX) return "This URL is too long (2,048 characters max).";
  if (!/^https:\/\//i.test(v)) return "The URL must start with https:// — CashTap only delivers over HTTPS.";
  try {
    const u = new URL(v);
    if (!u.hostname) throw new Error("no host");
  } catch {
    return "This doesn't look like a valid URL.";
  }
  return null;
}

function sameEvents(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((x) => set.has(x));
}

type FormAlert =
  | { kind: "error"; title?: string; message: string; requestId?: string | null }
  | { kind: "warn"; title?: string; message: string }
  | { kind: "rate"; message: string };

export interface EndpointFormModalProps {
  open: boolean;
  onClose: () => void;
  wallet: string | null;
  email: string | null;
  mode: "create" | "edit";
  /** The endpoint being edited (edit mode only). */
  endpoint?: WebhookEndpoint | null;
  /** Masked merchant email from GET /endpoints; null = none on file. */
  alertRecipient: string | null;
  /** Create succeeded — `secret` is shown ONCE by the caller, never stored. */
  onCreated?: (res: { endpoint: WebhookEndpoint; secret: string }) => void;
  /** Edit succeeded — the server's endpoint (stats carried over). */
  onSaved?: (endpoint: WebhookEndpoint) => void;
  /**
   * Create failed without any HTTP response (connection dropped): the POST
   * may still have landed. Resolve true if the endpoint now exists — the
   * modal then closes and the caller explains how to recover the secret.
   */
  onCreateUnconfirmed?: () => Promise<boolean>;
}

/**
 * Create / edit modal. Renders nothing while closed, so every opening starts
 * from a clean form.
 */
export default function EndpointFormModal(props: EndpointFormModalProps) {
  if (!props.open) return null;
  return <EndpointForm {...props} />;
}

function EndpointForm({
  onClose,
  wallet,
  email,
  mode,
  endpoint,
  alertRecipient,
  onCreated,
  onSaved,
  onCreateUnconfirmed,
}: EndpointFormModalProps) {
  const editing = mode === "edit" && endpoint ? endpoint : null;
  const formId = useId();
  const urlErrId = useId();
  const urlHintId = useId();
  const radioName = useId();
  const eventsErrId = useId();

  const initialAll = !editing || editing.events.includes("*");
  const [url, setUrl] = useState(editing?.url ?? "");
  const [description, setDescription] = useState(editing?.description ?? "");
  const [scope, setScope] = useState<"all" | "some">(initialAll ? "all" : "some");
  const [selected, setSelected] = useState<string[]>(() =>
    initialAll
      ? ["checkout.session.completed"]
      : (editing?.events ?? []).filter((e) => e !== "*"),
  );
  const [emailAlerts, setEmailAlerts] = useState(editing ? editing.emailAlerts : true);

  const [submitted, setSubmitted] = useState(false);
  const [urlServerError, setUrlServerError] = useState<string | null>(null);
  const [alert, setAlert] = useState<FormAlert | null>(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const urlRef = useRef<HTMLInputElement>(null);
  const cooldown = useCountdown();
  const { types } = useEventTypes(wallet, email);

  const trimmedUrl = url.trim();
  const urlChanged = !!editing && trimmedUrl !== editing.url;
  const urlError = urlServerError ?? (submitted ? validateUrl(url) : null);
  const events = scope === "all" ? ["*"] : sortEvents(selected);
  const eventsError =
    submitted && scope === "some" && events.length === 0
      ? "Pick at least one event, or choose All events."
      : null;
  const descriptionValue = description.trim() || null;

  const dirty =
    !editing ||
    urlChanged ||
    descriptionValue !== (editing.description ?? null) ||
    !sameEvents(events, editing.events) ||
    emailAlerts !== editing.emailAlerts;

  // The server re-resolves DNS for a new URL, so that is what the wait is.
  const checkingUrl = !editing || urlChanged;

  const handleError = (e: ApiError) => {
    const code = e.code || "";
    if (isUrlErrorCode(code)) {
      setUrlServerError(e.message || "This URL can't be used for webhooks.");
      urlRef.current?.focus();
    } else if (code === "endpoint_url_exists") {
      setUrlServerError("You already have an endpoint with this URL.");
      urlRef.current?.focus();
    } else if (code === "endpoint_limit_reached") {
      setAlert({
        kind: "error",
        title: "Endpoint limit reached",
        message: e.message || "You can have up to 5 endpoints.",
      });
    } else if (code === "webhooks_unavailable") {
      setAlert({ kind: "warn", message: UNAVAILABLE_MESSAGE });
    } else if (e.status === 429) {
      cooldown.start(e.retryAfter);
      setAlert({ kind: "rate", message: e.message || "Too many changes in a short time." });
    } else {
      setAlert({
        kind: "error",
        title: editing ? "Couldn't save your changes" : "Couldn't add the endpoint",
        message: e.message || "Something went wrong. Please try again.",
        requestId: e.requestId,
      });
    }
  };

  const submit = async (ev?: FormEvent) => {
    ev?.preventDefault();
    if (busyRef.current || cooldown.active) return;
    setSubmitted(true);
    setAlert(null);
    // A server URL error is re-checked by submitting again (dns_busy and
    // dns_timeout are transient), so only the client rules block here.
    setUrlServerError(null);
    if (validateUrl(url)) {
      urlRef.current?.focus();
      return;
    }
    if (scope === "some" && events.length === 0) return;
    if (editing && !dirty) {
      onClose();
      return;
    }

    busyRef.current = true;
    setBusy(true);
    try {
      if (editing) {
        const patch: Partial<{
          url: string;
          description: string | null;
          events: string[];
          emailAlerts: boolean;
        }> = {};
        if (urlChanged) patch.url = trimmedUrl;
        if (descriptionValue !== (editing.description ?? null)) patch.description = descriptionValue;
        if (!sameEvents(events, editing.events)) patch.events = events;
        if (emailAlerts !== editing.emailAlerts) patch.emailAlerts = emailAlerts;
        const res = await updateWebhookEndpoint(wallet, email, editing.id, patch);
        onSaved?.({ ...res.endpoint, stats24h: res.endpoint.stats24h ?? editing.stats24h });
        onClose();
      } else {
        const res = await createWebhookEndpoint(wallet, email, {
          url: trimmedUrl,
          description: descriptionValue,
          events,
          emailAlerts,
        });
        onCreated?.({ endpoint: res.endpoint, secret: res.secret });
        onClose();
      }
    } catch (err) {
      const e = apiErrorOf(err);
      // No HTTP status at all = the connection dropped. A create may still
      // have been committed; let the caller look before we say "failed".
      if (!editing && e.status === undefined && onCreateUnconfirmed) {
        const landed = await onCreateUnconfirmed().catch(() => false);
        if (landed) {
          onClose();
          return;
        }
        setAlert({
          kind: "error",
          title: "Couldn't reach CashTap",
          message: "Check your connection and try again. No endpoint was created.",
        });
        return;
      }
      handleError(e);
    } finally {
      busyRef.current = false;
      setBusy(false);
      notifyWebhooksChanged();
    }
  };

  const toggleEvent = (type: string) => {
    setSelected((cur) => (cur.includes(type) ? cur.filter((t) => t !== type) : [...cur, type]));
  };

  let submitLabel: ReactNode;
  if (busy) {
    submitLabel = (
      <>
        <Loader2 className="w-4 h-4 animate-spin" />
        {checkingUrl ? "Checking URL…" : "Saving…"}
      </>
    );
  } else if (cooldown.active) {
    submitLabel = (
      <>
        <Clock className="w-4 h-4" />
        {cooldown.text}
      </>
    );
  } else {
    submitLabel = editing ? (
      <>
        <Check className="w-4 h-4" />
        Save changes
      </>
    ) : (
      <>
        <Plus className="w-4 h-4" />
        Add endpoint
      </>
    );
  }

  const footer = (
    <>
      <button type="button" onClick={onClose} disabled={busy} className={ghostBtn}>
        Cancel
      </button>
      <button
        type="submit"
        form={formId}
        disabled={busy || cooldown.active || !dirty}
        className={`${primaryBtn} justify-center disabled:cursor-not-allowed`}
      >
        {submitLabel}
      </button>
    </>
  );

  return (
    <DevModal
      open
      onClose={onClose}
      title={editing ? "Edit endpoint" : "Add endpoint"}
      icon={editing ? <Pencil className="w-5 h-5" /> : <Send className="w-5 h-5" />}
      size="lg"
      busy={busy}
      footer={footer}
    >
      <form id={formId} onSubmit={submit} noValidate className="space-y-5">
        {alert && (alert.kind !== "rate" || cooldown.active) && (
          <InlineNotice
            tone={alert.kind === "error" ? "error" : "warn"}
            role="alert"
            title={alert.kind === "rate" ? "Slow down a little" : alert.title}
          >
            {alert.message}
            {alert.kind === "rate" && <span className="block mt-1 tabular-nums">{cooldown.text}.</span>}
            {alert.kind === "error" && alert.requestId && (
              <span className="block mt-1 text-[11px] font-mono opacity-70 break-all">
                Request ID · {alert.requestId}
              </span>
            )}
          </InlineNotice>
        )}

        <FormField
          label="Endpoint URL"
          error={urlError}
          errorId={urlErrId}
          hint={
            <span id={urlHintId}>
              HTTPS only, ports 443 or 8443. Must be a public domain — use a tunnel (ngrok,
              Cloudflare Tunnel) for local development.
            </span>
          }
        >
          <input
            ref={urlRef}
            type="url"
            inputMode="url"
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            maxLength={URL_MAX}
            value={url}
            readOnly={busy}
            onChange={(e) => {
              setUrl(e.target.value);
              setUrlServerError(null);
            }}
            placeholder={URL_PLACEHOLDER}
            aria-invalid={urlError ? true : undefined}
            aria-describedby={urlError ? urlErrId : urlHintId}
            className={`${inputCls} font-mono ${urlError ? "border-red-500/50 focus:border-red-400/60" : ""}`}
          />
        </FormField>

        {urlChanged && (
          <InlineNotice tone="warn" title="Changing the URL resets this endpoint's failure counters.">
            Deliveries still waiting to retry, and any resends, go to the new URL. CashTap checks the
            new address before saving.
          </InlineNotice>
        )}

        <FormField
          label="Description (optional)"
          hint={
            <span className="flex items-start justify-between gap-3">
              <span>Only you see this — for example "Production order service".</span>
              <span className="tabular-nums shrink-0">
                {description.length}/{DESCRIPTION_MAX}
              </span>
            </span>
          }
        >
          <input
            type="text"
            maxLength={DESCRIPTION_MAX}
            value={description}
            readOnly={busy}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Production order service"
            className={inputCls}
          />
        </FormField>

        <fieldset aria-describedby={eventsError ? eventsErrId : undefined}>
          <legend className="block text-[11px] uppercase tracking-[0.16em] text-gray-500 font-semibold mb-1.5">
            Events
          </legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <ScopeOption
              name={radioName}
              checked={scope === "all"}
              onSelect={() => setScope("all")}
              disabled={busy}
              title="All events"
              badge="Recommended"
              caption="Completed, expired and failed sessions."
            />
            <ScopeOption
              name={radioName}
              checked={scope === "some"}
              onSelect={() => setScope("some")}
              disabled={busy}
              title="Select events"
              caption="Only the event types you choose."
            />
          </div>

          {scope === "some" && (
            <div className="mt-2 space-y-2" role="group" aria-label="Event types">
              {types.map((t) => {
                const active = selected.includes(t.type);
                return (
                  <button
                    key={t.type}
                    type="button"
                    aria-pressed={active}
                    disabled={busy}
                    onClick={() => toggleEvent(t.type)}
                    className={`w-full flex items-start gap-3 p-3 rounded-xl border text-left transition-colors ${focusRing} ${
                      active
                        ? "bg-[#61CE70]/10 border-[#61CE70]/40 text-white"
                        : "bg-gray-900/30 border-gray-800 text-gray-400 hover:border-gray-700"
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`mt-0.5 w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${
                        active ? "bg-[#61CE70] border-[#61CE70]" : "border-gray-600"
                      }`}
                    >
                      {active && <Check className="w-3.5 h-3.5 text-black" strokeWidth={3} />}
                    </span>
                    <span className="min-w-0">
                      <span className="block font-mono text-xs sm:text-[13px] break-all">{t.type}</span>
                      {t.description && (
                        <span className="block text-xs text-gray-500 mt-0.5 leading-relaxed">
                          {t.description}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          {eventsError && (
            <p id={eventsErrId} role="alert" className="text-red-300 text-xs mt-1.5">
              {eventsError}
            </p>
          )}
        </fieldset>

        <div className="flex items-start justify-between gap-4 p-3.5 rounded-xl bg-gray-900/30 border border-gray-800/60">
          <div className="min-w-0">
            <p className="text-white text-sm font-medium">Email me when deliveries fail</p>
            <p className="text-gray-500 text-xs mt-0.5 leading-relaxed break-words">
              {alertRecipient ? (
                <>
                  Sent to <span className="text-gray-300">{alertRecipient}</span>. We always email you if
                  CashTap disables the endpoint.
                </>
              ) : (
                "No alert email on file — contact support to add one. Alerts still appear in your dashboard."
              )}
            </p>
          </div>
          <Switch
            checked={emailAlerts}
            onChange={setEmailAlerts}
            label="Email me when deliveries fail"
            disabled={busy}
          />
        </div>
        {/* Enter submits through the footer button (form={formId}). */}
      </form>
    </DevModal>
  );
}

function ScopeOption({
  name,
  checked,
  onSelect,
  disabled,
  title,
  badge,
  caption,
}: {
  name: string;
  checked: boolean;
  onSelect: () => void;
  disabled: boolean;
  title: string;
  badge?: string;
  caption: string;
}) {
  return (
    <label
      className={`flex items-start gap-3 p-3 rounded-xl border transition-colors ${
        checked
          ? "bg-[#61CE70]/10 border-[#61CE70]/40"
          : "bg-gray-900/30 border-gray-800 hover:border-gray-700"
      } ${disabled ? "cursor-not-allowed opacity-80" : "cursor-pointer"}`}
    >
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={onSelect}
        disabled={disabled}
        className="mt-0.5 w-4 h-4 shrink-0 accent-[#61CE70]"
      />
      <span className="min-w-0">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className={`text-sm font-medium ${checked ? "text-white" : "text-gray-300"}`}>{title}</span>
          {badge && (
            <span className="text-[10px] uppercase tracking-wide font-bold text-[#61CE70]">{badge}</span>
          )}
        </span>
        <span className="block text-xs text-gray-500 mt-0.5 leading-relaxed">{caption}</span>
      </span>
    </label>
  );
}
